// src/utils/errorMapping.js

export const mapAuthErrors = (error) => {
  // Default fallback error message
  let message = "An unexpected error occurred. Please try again.";

  if (!error.response) {
    // No response means a network error or CORS issue
    message = "Network error. Please check your connection.";
  } else {
    const { status, data } = error.response;
    // Handle both ErrorResponse and simple message structures
    const backendMessage =
      data?.error?.message || data?.error || data?.message || "";

    switch (status) {
      case 400:
        // BAD REQUEST: Possibly validation issues or incorrect CAPTCHA
        if (backendMessage) {
          const lowerMsg = backendMessage.toLowerCase();
          if (lowerMsg.includes("captcha")) {
            message = "Incorrect CAPTCHA. Please try again.";
          } else {
            message = backendMessage;
          }
        } else {
          message = "Invalid request. Please check your inputs.";
        }
        break;

      case 401:
        // UNAUTHORIZED: This can include wrong credentials or usage limits.
        if (backendMessage) {
          const lowerMsg = backendMessage.toLowerCase();

          // Check usage limit messages first:
          if (lowerMsg.includes("guest limit reached")) {
            message =
              "You have reached the guest usage limit. Please register for a full account to continue accessing more features.";
          } else if (
            lowerMsg.includes("free lifetime limit") ||
            lowerMsg.includes("free user")
          ) {
            message =
              "You have reached your free user limit. Consider upgrading to a paid plan or subscription for unlimited access.";
          } else if (lowerMsg.includes("user limit reached")) {
            message =
              "You have reached your current usage limit. Please upgrade your plan or contact support for more options.";
          }
          // Then check other login-related errors:
          else if (lowerMsg.includes("guests cannot log in")) {
            message =
              "Guests cannot log in. Please register or log in to a full account for more features.";
          } else if (lowerMsg.includes("invalid username or password")) {
            message = "Wrong username or password. Please try again.";
          } else {
            // If no known phrase matched, just use the backendMessage.
            message = backendMessage;
          }
        } else {
          // If no backendMessage is provided at all, fallback to a generic message.
          message =
            "You have made too many requests. Please wait and try again later.";
        }
        break;

      case 403:
        // FORBIDDEN: The user doesn't have permission for the requested resource
        message =
          backendMessage ||
          "You do not have permission to access this resource.";
        break;

      case 404:
        // NOT FOUND: The requested resource doesn't exist
        message = backendMessage || "Requested resource not found.";
        break;

      case 409:
        // CONFLICT: Username taken, session conflict, etc.
        if (backendMessage) {
          const lowerMsg = backendMessage.toLowerCase();
          if (lowerMsg.includes("active session")) {
            message =
              "Another session is already open with your account. Please close other sessions first.";
          } else {
            message = backendMessage;
          }
        } else {
          message = "Conflict occurred. Please try again.";
        }
        break;

      case 429:
        // TOO MANY REQUESTS: Rate-limiting or usage limit reached
        if (backendMessage) {
          const lowerMsg = backendMessage.toLowerCase();
          if (lowerMsg.includes("guest limit reached")) {
            message =
              "You have reached the guest usage limit. Please register for a full account to continue accessing more features.";
          } else if (
            lowerMsg.includes("free lifetime limit") ||
            lowerMsg.includes("free user")
          ) {
            message =
              "You have reached your free user limit. Consider upgrading to a paid plan or subscription for unlimited access.";
          } else if (lowerMsg.includes("user limit reached")) {
            message =
              "You have reached your current usage limit. Please upgrade your plan or contact support for more options.";
          } else {
            message = backendMessage;
          }
        } else {
          message =
            "You have made too many requests. Please wait and try again later.";
        }
        break;

      case 500:
        // INTERNAL SERVER ERROR: Something broke on the backend
        message = backendMessage || "Server error. Please try again later.";
        break;

      default:
        // Any other HTTP status code not specifically handled above
        message = backendMessage || message;
        break;
    }
  }

  return message;
};
